import { useEffect, useRef, useState } from 'react';
import AppNumberInput from 'components/FormElements/AppNumberInput';
import api from 'lib/trpcClient';
import { useNotification } from 'hooks/ui/useNotification';
import { TRPCClientErrorLike } from '@trpc/client';
import { ApiRouter } from 'further-api/trpc/routes';
import { round } from 'lodash';

type Props = any; //TODO: define types

const PerformanceFeeCell: React.FC<Props> = ({
  shareholdingId,
  totalSaleValue,
  setOverride,
  performanceFee,
  disableTable,
  fromUpload,
}) => {
  const notification = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const previousTotalSaleValue = useRef(totalSaleValue);
  const previousShareholdingId = useRef(shareholdingId);
  const previousDisableTable = useRef(disableTable);
  const apiUtils = api.useUtils();
  // Store the fetch function in a ref to avoid unstable dependency
  // tRPC utils are stable, but we use a ref to avoid dependency array issues
  const calculatePerformanceFeeRef = useRef(
    apiUtils.exit.calculatePerformanceFee.fetch,
  );
  // Update ref on each render to ensure we have the latest function
  calculatePerformanceFeeRef.current =
    apiUtils.exit.calculatePerformanceFee.fetch;

  useEffect(() => {
    const fetchPerformanceFee = async () => {
      if (totalSaleValue > 0 && !disableTable) {
        setIsLoading(true);
        try {
          const roundedTotalSaleValue = round(totalSaleValue, 2);
          const data = await calculatePerformanceFeeRef.current({
            shareholdingId,
            totalSaleValue: roundedTotalSaleValue,
          });

          setOverride(shareholdingId, {
            performanceFee: Number(data.totalPerformanceFee),
          });
        } catch (error: unknown) {
          const trpcError = error as TRPCClientErrorLike<ApiRouter>;
          notification.error(
            trpcError?.data?.cause ??
              trpcError?.message ??
              'Error calculating performance fee.',
          );
        } finally {
          setIsLoading(false);
        }
      }
    };

    // Round both values to 2 decimal places before comparison to avoid floating point errors
    // This prevents false positives when values like 397.60 vs 397.59999999999854 are compared
    const roundedTotalSaleValue = round(totalSaleValue, 2);
    const roundedPreviousTotalSaleValue = round(
      previousTotalSaleValue.current,
      2,
    );
    const totalSaleValueChanged =
      roundedPreviousTotalSaleValue !== roundedTotalSaleValue;
    const shareholdingIdChanged =
      previousShareholdingId.current !== shareholdingId;
    const tableEnabledAgain =
      previousDisableTable.current === true && disableTable === false;

    // If this data came from an upload, don't recalculate - respect the value from the file
    if (fromUpload) {
      // Clear the upload flag so future manual edits will trigger recalculation
      setOverride(shareholdingId, { _fromUpload: false });
      // Store rounded value to prevent floating point comparison issues
      previousTotalSaleValue.current = roundedTotalSaleValue;
    } else if (
      totalSaleValueChanged ||
      shareholdingIdChanged ||
      tableEnabledAgain
    ) {
      fetchPerformanceFee();
      // Store rounded value to prevent floating point comparison issues
      previousTotalSaleValue.current = roundedTotalSaleValue;
    } else {
      // Even if nothing changed, update the ref with rounded value to prevent drift
      previousTotalSaleValue.current = roundedTotalSaleValue;
    }

    previousShareholdingId.current = shareholdingId;
    previousDisableTable.current = disableTable;
  }, [
    totalSaleValue,
    shareholdingId,
    disableTable,
    setOverride,
    notification,
    fromUpload,
  ]);

  if (isLoading) return <>{'...'}</>;

  return (
    <AppNumberInput
      disabled={disableTable}
      decimals={2}
      defaultValue={0}
      min={0}
      currencyStartAdornment
      value={performanceFee}
      onChange={(newValue) => {
        setOverride(shareholdingId, {
          performanceFee: Number(newValue),
        });
      }}
    />
  );
};

export default PerformanceFeeCell;
